import React, {useState, useEffect} from 'react';
import {
    MessageCircle,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Trash2,
    Search,
    Mail,
    User,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Filter
} from 'lucide-react';

const FeedbacksPage = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDate, setFilterDate] = useState('');
    const [expandedFeedback, setExpandedFeedback] = useState(null);

    useEffect(() => {
        fetchFeedbacks();
    }, [page, pageSize]);

    const fetchFeedbacks = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/view_feedback/?page=${page}&page_size=${pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch feedbacks: ${response.statusText}`);
            }

            const data = await response.json();
            setFeedbacks(data);
        } catch (error) {
            console.error('Error fetching feedbacks:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteFeedback = async (feedbackId) => {
        const confirmed = window.confirm('Are you sure you want to delete this feedback?');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/delete_feedback/${feedbackId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to delete feedback: ${response.statusText}`);
            }

            // Remove the deleted feedback from the state
            setFeedbacks(prevFeedbacks =>
                // FIX 1: Use _id for filtering
                prevFeedbacks.filter(feedback => feedback._id !== feedbackId)
            );

            alert("Feedback deleted successfully");
        } catch (error) {
            console.error('Error deleting feedback:', error);
            alert(`Failed to delete feedback: ${error.message}`);
        }
    };

    const toggleExpandFeedback = (id) => {
        // FIX 1: Use _id to check for expansion
        if (expandedFeedback === id) {
            setExpandedFeedback(null);
        } else {
            setExpandedFeedback(id);
        }
    };

    // Filter feedbacks by search term and date
    const filteredFeedbacks = feedbacks.filter(feedback => {
        const matchesSearch =
            feedback.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            feedback.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            feedback.message.toLowerCase().includes(searchTerm.toLowerCase());

        if (!filterDate) return matchesSearch;

        // Convert both dates to yyyy-mm-dd format for comparison
        // FIX 2: Use submitted_at instead of created_at
        const feedbackDate = new Date(feedback.submitted_at).toISOString().split('T')[0];
        return matchesSearch && feedbackDate === filterDate;
    });

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <MessageCircle className="text-blue-600" size={32}/>
                    User Feedbacks
                </h1>
                <p className="mt-2 text-gray-600">
                    Review and manage user feedback submissions
                </p>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
                        <div className="flex items-center gap-2">
                            <label htmlFor="page" className="text-sm font-medium text-gray-700">
                                Page:
                            </label>
                            <input
                                id="page"
                                type="number"
                                min="1"
                                value={page}
                                onChange={(e) => setPage(Math.max(1, Number(e.target.value)))}
                                className="w-16 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">
                                Results:
                            </label>
                            <select
                                id="pageSize"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {[10, 25, 50, 100].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        <div className="relative w-full sm:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={16} className="text-gray-400"/>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search feedbacks..."
                                className="pl-10 w-full border border-gray-300 rounded-md py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <Filter size={14} className="text-gray-500"/>
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button
                            onClick={fetchFeedbacks}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Feedbacks List */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading feedbacks</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredFeedbacks.length > 0 ? (
                        filteredFeedbacks.map((feedback) => (
                            // FIX 1: Use _id as the key
                            <div key={feedback._id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div
                                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                                    // FIX 1: Use _id to toggle
                                    onClick={() => toggleExpandFeedback(feedback._id)}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start gap-4">
                                                <div
                                                    className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <User size={20} className="text-blue-600"/>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-medium text-gray-900">{feedback.name}</h3>
                                                    <div
                                                        className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-gray-500">
                                                        <div className="flex items-center">
                                                            <Mail size={14} className="mr-1"/>
                                                            {feedback.email}
                                                        </div>
                                                        <div className="flex items-center">
                                                            <Calendar size={14} className="mr-1"/>
                                                            {/* FIX 2: Use submitted_at to display the date */}
                                                            {new Date(feedback.submitted_at).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                // FIX 1: Use _id for checking expansion
                                                className={`mt-4 ${expandedFeedback === feedback._id ? '' : 'line-clamp-2'}`}>
                                                <p className="text-gray-700">
                                                    {feedback.message}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // FIX 1: Pass _id to the delete function
                                                    deleteFeedback(feedback._id);
                                                }}
                                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                                            >
                                                <Trash2 size={14} className="mr-1"/>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            {searchTerm || filterDate ? (
                                <div className="flex flex-col items-center">
                                    <Search size={40} className="text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No feedbacks found</h3>
                                    <p className="text-gray-600 mb-4">
                                        No feedbacks match your current search filters
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setFilterDate('');
                                        }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                        Clear all filters
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <MessageCircle size={40} className="text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No feedbacks available</h3>
                                    <p className="text-gray-600">
                                        There are no feedback submissions to display
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {filteredFeedbacks.length > 0 && (
                        <div className="flex items-center justify-between bg-white px-6 py-3 rounded-xl shadow-sm">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={filteredFeedbacks.length < pageSize}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{filteredFeedbacks.length}</span> results
                                        {pageSize < filteredFeedbacks.length && (
                                            <> (limited to <span className="font-medium">{pageSize}</span> per page)</>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                         aria-label="Pagination">
                                        <button
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                            disabled={page === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <ChevronLeft className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                        <span
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {page}
                    </span>
                                        <button
                                            onClick={() => setPage(page + 1)}
                                            disabled={filteredFeedbacks.length < pageSize}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Next</span>
                                            <ChevronRight className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FeedbacksPage;